import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Heading } from '../../components';
import { FormattedMessage, useIntl } from 'react-intl';

import { getSpecificationItems } from './listingSectionUtils';

import css from './ListingPage.module.css';

const SectionSpecifications = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    status,
    listingState,
    condition,
  } = props;
  const intl = useIntl();
  const [otherExpanded, setOtherExpanded] = useState(false);

  const { coreItems, engineGroup, otherGroup } = useMemo(
    () =>
      getSpecificationItems({
        publicData,
        metadata,
        listingFieldConfigs,
        categoryConfiguration,
        status,
        listingState,
        condition,
        intl,
      }),
    [
      publicData,
      metadata,
      listingFieldConfigs,
      categoryConfiguration,
      status,
      listingState,
      condition,
      intl,
    ]
  );

  const hasCore = coreItems?.length;
  const hasEngines =
    engineGroup &&
    (engineGroup.listingItems?.length || engineGroup.subgroups?.some(group => group.items?.length));
  const otherItemCount =
    otherGroup?.subgroups?.reduce((sum, group) => sum + (group.items?.length || 0), 0) || 0;
  const hasOther = otherItemCount > 0;

  if (!hasCore && !hasEngines && !hasOther) {
    return null;
  }

  return (
    <div className={classNames([css.customFields, css.specifications])} id="listing-specifications">
      <Heading as="h2" rootClassName={classNames(css.sectionHeading, css.sectionHeadingColored)}>
        <FormattedMessage id="SectionSpecifications.title" />
      </Heading>
      <div className={css.sectionSubheading}>
        <FormattedMessage id="SectionSpecifications.subtitle" />
      </div>
      <div className={css.specsGroups}>
        {hasCore ? (
          <dl className={classNames(css.specsList, css.specsCoreList)}>
            {coreItems.map(item => (
              <div key={item.key} className={css.specsRow}>
                <dt className={css.specsLabel}>{item.label}</dt>
                <dd className={css.specsValue}>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
      {hasEngines ? (
        <div className={classNames(css.specsGroup, css.specsGroupEngines)}>
          <div className={classNames(css.specsGroupTitle, css.specsGroupTitleEngines)}>
            <FormattedMessage id={engineGroup.titleId} />
          </div>
          <div className={css.specsGroupBodyEngines}>
            {engineGroup.listingItems?.length ? (
              <div className={classNames(css.specsSubgroup, css.specsSubgroupEnginesListing)}>
                <dl className={css.specsList}>
                  {engineGroup.listingItems.map(item => (
                    <div key={item.key} className={css.specsRow}>
                      <dt className={css.specsLabel}>{item.label}</dt>
                      <dd className={css.specsValue}>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
            {engineGroup.subgroups?.map(group => (
              <div key={group.key} className={classNames(css.specsSubgroup, css.specsSubgroupEngine)}>
                <div className={css.specsSubgroupTitle}>
                  <FormattedMessage id={group.titleId} />
                </div>
                {group.summary ? (
                  <div className={css.specsEngineSummary}>{group.summary}</div>
                ) : (
                  <dl className={css.specsList}>
                    {group.items.map(item => (
                      <div key={item.key} className={css.specsRow}>
                        <dt className={css.specsLabel}>{item.label}</dt>
                        <dd className={css.specsValue}>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {hasOther && otherExpanded ? (
        <div className={css.specsGroup}>
          <div className={css.specsGroupTitle}>
            <FormattedMessage id={otherGroup.titleId} />
          </div>
          {otherGroup.subgroups?.map(group => (
            <div key={group.key} className={classNames(css.specsSubgroup, css.specsGroupIndented)}>
              <div className={css.specsSubgroupTitle}>
                <FormattedMessage id={group.titleId} />
              </div>
              <dl className={css.specsList}>
                {group.items.map(item => (
                  <div key={item.key} className={css.specsRow}>
                    <dt className={css.specsLabel}>{item.label}</dt>
                    <dd className={css.specsValue}>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      ) : null}
      {hasOther ? (
        <button
          type="button"
          className={css.equipmentToggle}
          onClick={() => setOtherExpanded(prev => !prev)}
        >
          {otherExpanded ? (
            <FormattedMessage id="SectionSpecifications.showLess" />
          ) : (
            <FormattedMessage
              id="SectionSpecifications.showAllOther"
              values={{ count: otherItemCount }}
            />
          )}
        </button>
      ) : null}
    </div>
  );
};

export default SectionSpecifications;
